import React, { useState, useEffect } from 'react';
import ClientSidebar from '../../components/ClientSidebar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { justificatifService } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Upload, CheckCircle, Clock, XCircle, FileText, Trash2, Eye } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const JUSTIFICATIF_TYPES = [
  {
    type: 'piece_identite',
    label: 'Pièce d\'identité',
    description: 'Carte d\'identité, passeport ou permis de conduire',
    icon: '🪪'
  },
  {
    type: 'rib',
    label: 'RIB',
    description: 'Relevé d\'Identité Bancaire',
    icon: '🏦'
  },
  {
    type: 'justificatif_domicile',
    label: 'Justificatif de domicile',
    description: 'Facture d\'électricité, gaz, eau ou quittance de loyer (moins de 3 mois)',
    icon: '🏠'
  },
  {
    type: 'avis_imposition',
    label: 'Avis d\'imposition',
    description: 'Dernier avis d\'imposition sur le revenu',
    icon: '📋'
  },
  {
    type: 'releve_compte',
    label: 'Relevé de compte',
    description: 'Derniers relevés de compte bancaire (3 derniers mois)',
    icon: '📊'
  }
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MesJustificatifs = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [justificatifs, setJustificatifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingType, setUploadingType] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewName, setPreviewName] = useState('');

  useEffect(() => {
    if (user?.id) {
      loadJustificatifs();
    }
  }, [user]);

  const loadJustificatifs = async () => {
    try {
      const data = await justificatifService.getByClient(user.id);
      setJustificatifs(data);
    } catch (error) {
      console.error('Error loading justificatifs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (type, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'Fichier trop volumineux',
        description: 'La taille maximale est de 10 MB',
        variant: 'destructive',
      });
      return;
    }

    setUploadingType(type);

    try {
      const result = await justificatifService.upload(user.id, type, file);
      
      // Update local state
      const existingIndex = justificatifs.findIndex(j => j.type === type);
      if (existingIndex >= 0) {
        const updated = [...justificatifs];
        updated[existingIndex] = result;
        setJustificatifs(updated);
      } else {
        setJustificatifs([...justificatifs, result]);
      }

      toast({
        title: 'Document uploadé',
        description: `${file.name} a été ajouté avec succès`,
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Erreur d\'upload',
        description: 'Impossible d\'uploader le fichier',
        variant: 'destructive',
      });
    } finally {
      setUploadingType(null);
    }
  };

  const handleDelete = async (justificatifId, type) => {
    try {
      await justificatifService.delete(justificatifId);
      setJustificatifs(justificatifs.filter(j => j.id !== justificatifId));
      toast({
        title: 'Document supprimé',
        description: 'Le document a été supprimé avec succès',
      });
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le document',
        variant: 'destructive',
      });
    }
  };

  const handlePreview = (justif) => {
    // Construct the stored filename: type_fileName
    // For backwards compatibility, use storedFileName if available, otherwise construct it
    const storedFileName = justif.storedFileName || `${justif.type}_${justif.fileName}`;
    const fileUrl = `${API_URL}/api/justificatifs/file/${user.id}/${storedFileName}`;
    setPreviewUrl(fileUrl);
    setPreviewName(justif.fileName);
    setPreviewOpen(true);
  };

  const getJustificatifStatus = (type) => {
    const justif = justificatifs.find(j => j.type === type);
    return justif;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Validé':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'En attente':
        return <Clock className="h-5 w-5 text-orange-500" />;
      case 'Refusé':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Validé': 'bg-green-100 text-green-700',
      'En attente': 'bg-orange-100 text-orange-700',
      'Refusé': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const completionPercentage = () => {
    const total = JUSTIFICATIF_TYPES.length;
    const completed = justificatifs.filter(j => j.status === 'Validé').length;
    return Math.round((completed / total) * 100);
  };

  return (
    <div className="flex">
      <ClientSidebar />
      <div className="flex-1 bg-gray-50">
        {/* Header */}
        <div className="bg-gray-800 text-white py-3 px-8 text-sm">
          Un crédit vous engage et doit être remboursé. Vérifiez vos capacités de remboursement avant de vous engager.
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto p-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Mes justificatifs</h1>
            <p className="text-gray-600">
              Complétez votre dossier en ajoutant les documents demandés
            </p>
          </div>

          {/* Progress Card */}
          <Card className="p-6 mb-8 bg-gradient-to-r from-cyan-50 to-blue-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900 mb-1">
                  Complétion du dossier : {completionPercentage()}%
                </h3>
                <p className="text-sm text-gray-600">
                  {justificatifs.filter(j => j.status === 'Validé').length} sur {JUSTIFICATIF_TYPES.length} documents validés
                </p>
              </div>
              <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center">
                <span className="text-2xl font-bold text-purple-600">{completionPercentage()}%</span>
              </div>
            </div>
            <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
              <div
                className="h-2 rounded-full transition-all duration-500"
                style={{
                  width: `${completionPercentage()}%`,
                  backgroundColor: '#8B5CF6'
                }}
              ></div>
            </div>
          </Card>

          {/* Documents List */}
          <div className="space-y-4">
            {JUSTIFICATIF_TYPES.map((type) => {
              const existingJustif = getJustificatifStatus(type.type);
              const isUploading = uploadingType === type.type;

              return (
                <Card key={type.type} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="text-4xl">{type.icon}</div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 mb-1">{type.label}</h3>
                        <p className="text-sm text-gray-600 mb-3">{type.description}</p>
                        
                        {existingJustif && (
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center space-x-2">
                              <FileText className="h-4 w-4 text-gray-500" />
                              <span className="text-sm text-gray-700">{existingJustif.fileName}</span>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(existingJustif.status)}`}>
                              {existingJustif.status}
                            </span>
                            {getStatusIcon(existingJustif.status)}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {existingJustif ? (
                        <>
                          {/* Eye button to preview */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            onClick={() => handlePreview(existingJustif)}
                            title="Voir le document"
                          >
                            <Eye className="h-5 w-5" />
                          </Button>
                          <input
                            type="file"
                            id={`file-${type.type}`}
                            className="hidden"
                            onChange={(e) => handleFileUpload(type.type, e)}
                            accept=".pdf,.jpg,.jpeg,.png"
                          />
                          <label htmlFor={`file-${type.type}`}>
                            <Button
                              variant="outline"
                              className="cursor-pointer"
                              disabled={isUploading}
                              asChild
                            >
                              <span>
                                <Upload className="h-4 w-4 mr-2" />
                                Remplacer
                              </span>
                            </Button>
                          </label>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(existingJustif.id, type.type)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <input
                            type="file"
                            id={`file-${type.type}`}
                            className="hidden"
                            onChange={(e) => handleFileUpload(type.type, e)}
                            accept=".pdf,.jpg,.jpeg,.png"
                          />
                          <label htmlFor={`file-${type.type}`}>
                            <Button
                              className="text-white cursor-pointer"
                              style={{ backgroundColor: '#8B5CF6' }}
                              disabled={isUploading}
                              asChild
                            >
                              <span>
                                <Upload className="h-4 w-4 mr-2" />
                                {isUploading ? 'Upload en cours...' : 'Ajouter'}
                              </span>
                            </Button>
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Help Card */}
          <Card className="mt-8 p-6 bg-purple-50 border-purple-200">
            <div className="flex items-start space-x-4">
              <div className="p-3 rounded-lg bg-white">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  Formats acceptés
                </h3>
                <p className="text-sm text-gray-600">
                  PDF, JPG, JPEG, PNG - Taille maximale : 10 MB par fichier
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Une fois tous vos documents validés, votre dossier sera traité dans les plus brefs délais.
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-purple-600" />
                <span>{previewName}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4 flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden" style={{ minHeight: '400px' }}>
              {previewUrl && (
                previewName.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={previewUrl}
                    className="w-full h-[70vh]"
                    title="Document preview"
                  />
                ) : (
                  <img
                    src={previewUrl}
                    alt={previewName}
                    className="max-w-full max-h-[70vh] object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<p class="text-gray-500">Impossible de charger l\'aperçu</p>';
                    }}
                  />
                )
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MesJustificatifs;
